import React from 'react';
import styled from 'styled-components';
import { GitBranch, Check } from 'lucide-react';
import { ProcessStageDto, ProcessStageStatus, getStageStatusColor } from '../../services/committeesService';

interface CompactTimelineProps {
  stages: ProcessStageDto[];
  onViewFull?: () => void;
}

const Container = styled.div`
  padding: 20px;
  /* No box styling - unified canvas */

  @media (max-width: 767px) {
    padding: 16px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
`;

const Title = styled.h3`
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TitleIcon = styled.span`
  color: var(--text-secondary);
  display: flex;
  align-items: center;
`;

const ViewFullButton = styled.button`
  background: none;
  border: 1px solid var(--primary-color);
  color: var(--primary-color);
  padding: 6px 12px;
  border-radius: var(--border-radius-sm);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--primary-color);
    color: white;
  }
`;

const TimelineWrapper = styled.div`
  overflow-x: auto;
  margin: 0 -20px;
  padding: 0 20px;

  @media (max-width: 767px) {
    margin: 0 -16px;
    padding: 0 16px;
  }
`;

const TimelineList = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 0;
  min-width: max-content;
  padding: 8px 0;
`;

const StageItem = styled.div<{ $status: ProcessStageStatus; $isLast: boolean; $isFirst: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 100px;
  flex: 1;

  &::before {
    content: '';
    position: absolute;
    top: 12px;
    left: 50%;
    width: ${props => props.$isFirst ? '0' : '100%'};
    height: 2px;
    background: ${props => {
      if (props.$status === ProcessStageStatus.Completed) return '#94a3b8'; /* Muted gray for completed */
      if (props.$status === ProcessStageStatus.Current) return 'linear-gradient(to right, #94a3b8, var(--primary-color))';
      return 'rgba(0, 0, 0, 0.08)';
    }};
    z-index: 0;
  }

  @media (max-width: 767px) {
    min-width: 80px;

    &::before {
      top: 10px;
    }
  }
`;

const StageDot = styled.div<{ $status: ProcessStageStatus }>`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${props => {
    if (props.$status === ProcessStageStatus.Pending) return 'white';
    if (props.$status === ProcessStageStatus.Current) return 'white';
    if (props.$status === ProcessStageStatus.Completed) return '#94a3b8'; /* Muted gray for completed */
    return getStageStatusColor(props.$status);
  }};
  border: 2px solid ${props => {
    if (props.$status === ProcessStageStatus.Pending) return 'rgba(0, 0, 0, 0.12)';
    if (props.$status === ProcessStageStatus.Completed) return '#94a3b8'; /* Muted gray border */
    return getStageStatusColor(props.$status);
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  position: relative;
  z-index: 1;
  color: ${props => props.$status === ProcessStageStatus.Completed ? 'white' : getStageStatusColor(props.$status)};

  ${props => props.$status === ProcessStageStatus.Current && `
    box-shadow: 0 0 0 5px rgba(0, 115, 234, 0.2);
    width: 30px;
    height: 30px;
    border-width: 3px;
    border-color: var(--primary-color);
  `}

  @media (max-width: 767px) {
    width: 20px;
    height: 20px;

    ${props => props.$status === ProcessStageStatus.Current && `
      width: 26px;
      height: 26px;
    `}
  }
`;

const CurrentDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: var(--primary-color);
  animation: pulse 1.5s ease-in-out infinite;

  @keyframes pulse {
    0%, 100% {
      transform: scale(1);
      opacity: 1;
    }
    50% {
      transform: scale(0.85);
      opacity: 0.8;
    }
  }
`;

const StageContent = styled.div<{ $status: ProcessStageStatus }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  margin-top: 8px;
  text-align: center;
`;

const StageName = styled.span<{ $status: ProcessStageStatus }>`
  font-size: 11px;
  font-weight: ${props => props.$status === ProcessStageStatus.Current ? '700' : '500'};
  color: ${props => {
    if (props.$status === ProcessStageStatus.Current) return 'var(--primary-color)';
    if (props.$status === ProcessStageStatus.Completed) return '#64748b'; /* Muted text for completed */
    return 'var(--text-secondary)';
  }};
  line-height: 1.3;
  max-width: 90px;
  word-wrap: break-word;

  @media (max-width: 767px) {
    font-size: 10px;
    max-width: 70px;
  }
`;

const StageDate = styled.span`
  font-size: 10px;
  color: var(--text-secondary);
  font-weight: 400;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 32px 20px;
  color: var(--text-secondary);
  font-size: 14px;
`;

const CompactTimeline: React.FC<CompactTimelineProps> = ({ stages, onViewFull }) => {
  const formatDate = (dateString?: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('he-IL', {
      day: '2-digit',
      month: '2-digit'
    });
  };

  // Get compact view: 2 completed, 1 current, 2 pending
  const getCompactStages = (): ProcessStageDto[] => {
    const currentIndex = stages.findIndex(s => s.status === ProcessStageStatus.Current);

    if (currentIndex === -1) {
      // No current stage, show first 5
      return stages.slice(0, 5);
    }

    // Get 2 before current, current, and 2 after
    const start = Math.max(0, currentIndex - 2);
    const end = Math.min(stages.length, currentIndex + 3);

    return stages.slice(start, end);
  };

  const compactStages = getCompactStages();

  if (stages.length === 0) {
    return (
      <Container>
        <Header>
          <Title>
            <TitleIcon><GitBranch size={16} strokeWidth={1.5} /></TitleIcon>
            ציר זמן תהליך
          </Title>
        </Header>
        <EmptyState>לא נמצאו שלבי תהליך</EmptyState>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>
          <TitleIcon><GitBranch size={16} strokeWidth={1.5} /></TitleIcon>
          ציר זמן תהליך
        </Title>
        {onViewFull && (
          <ViewFullButton onClick={onViewFull}>
            תצוגה מלאה
          </ViewFullButton>
        )}
      </Header>
      <TimelineWrapper>
        <TimelineList>
          {compactStages.map((stage, index) => (
            <StageItem
              key={stage.id}
              $status={stage.status}
              $isLast={index === compactStages.length - 1}
              $isFirst={index === 0}
            >
              <StageDot $status={stage.status}>
                {stage.status === ProcessStageStatus.Completed && (
                  <Check size={14} strokeWidth={2.5} />
                )}
                {stage.status === ProcessStageStatus.Current && (
                  <CurrentDot />
                )}
              </StageDot>
              <StageContent $status={stage.status}>
                <StageName $status={stage.status}>
                  {stage.stageName}
                </StageName>
                {stage.stageDate && (
                  <StageDate>{formatDate(stage.stageDate)}</StageDate>
                )}
              </StageContent>
            </StageItem>
          ))}
        </TimelineList>
      </TimelineWrapper>
    </Container>
  );
};

export default CompactTimeline;
